import type { OverviewRow } from '../../types/kyckConnectOverview.js';
import type { ServiceBadgeId } from './services.js';

// THE OVERVIEW TABLE'S FILTERING AND SORTING (redesign Phase 1). PURE.
//
// ⚠️ A missing figure (no leads, calls off) is NULL, and a null always sorts LAST in both directions: a dash is "nothing to
// compare", never a zero that would top or bottom the list.

export type OverviewSortKey = 'client' | 'leads' | 'contacted' | 'speed' | 'booked' | 'needsPerson' | 'status';
export type SortDir = 'asc' | 'desc';

export interface OverviewFilters {
  brandId: string | null;
  service: ServiceBadgeId | null;
}

export function filterRows(rows: readonly OverviewRow[], f: OverviewFilters): OverviewRow[] {
  return rows.filter((r) => (f.brandId === null || r.brandId === f.brandId) && (f.service === null || r.badges.some((b) => b.id === f.service)));
}

/** Status order for sorting: setup problems first, then clients with work waiting, then healthy ones. */
const statusRank = (r: OverviewRow): number => (r.health.kind === 'incomplete' ? 0 : r.health.kind === 'waiting' ? 1 : 2);

function valueOf(r: OverviewRow, key: OverviewSortKey): number | string | null {
  switch (key) {
    case 'client': return r.accountName.toLowerCase();
    case 'leads': return r.leads;
    case 'contacted': return r.contactedPct;
    case 'speed': return r.medianSpeedSeconds;
    case 'booked': return r.booked;
    case 'needsPerson': return r.needsPerson;
    case 'status': return statusRank(r);
  }
}

export function sortRows(rows: readonly OverviewRow[], key: OverviewSortKey, dir: SortDir): OverviewRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = valueOf(a, key);
    const y = valueOf(b, key);
    if (x === null && y === null) return a.accountName.localeCompare(b.accountName);
    if (x === null) return 1;
    if (y === null) return -1;
    const c = typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y) : (x as number) - (y as number);
    return c !== 0 ? c * sign : a.accountName.localeCompare(b.accountName);
  });
}

/** The brands that appear in the rows, for the filter menu. */
export function brandOptions(rows: readonly OverviewRow[]): Array<{ value: string; label: string }> {
  const m = new Map<string, string>();
  for (const r of rows) if (r.brandId && r.brandName) m.set(r.brandId, r.brandName);
  return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
}

/** "just now", "3 hours", "2 days": the age of the oldest waiting item, in words. */
export function ageInWords(iso: string | null, nowMs: number): string | null {
  if (!iso) return null;
  const ms = nowMs - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'under a minute';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} days`;
}
